import { format } from "date-fns";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Skeleton } from "primereact/skeleton";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import AdminOnly from "~/components/AdminOnly";
import Menu from "~/components/menu/Menu";
import {
  OtherEntityAppointment,
  OtherEntitiesAppointmentTableProps,
} from "~/Pages/Directors/types/director";
import { fetchAllEntites } from "~/Pages/MyEntityDashboard/myEntityDashboardSlice";

const OtherEntitiesAppointmentTable = ({
  appointments,
  loading,
  onEditAppointment,
  onDeleteAppointment,
}: OtherEntitiesAppointmentTableProps) => {
  const skeletonRows = [...Array(5)];
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const dispatch = useDispatch();

  // Get user's admin status from Redux store
  const { isAdmin } = useSelector((state: any) => state.login);
  // Get the list of entities the user has access to from myentitydashboard
  const allEntities = useSelector(
    (state: any) => state.myentitydashboard?.allentites || [],
  );

  // Fetch entities if not already loaded (needed for entity access check)
  useEffect(() => {
    if (!isAdmin && allEntities.length === 0) {
      dispatch(fetchAllEntites() as any);
    }
  }, [dispatch, isAdmin, allEntities.length]);

  const onPageChange = (event: any) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  // Check if user has access to a specific entity by checking if it exists in their accessible entities list
  const hasEntityAccess = (entityId: string | undefined): boolean => {
    if (!entityId) return false;
    if (isAdmin) return true;
    // Check if the entity ID exists in the user's accessible entities
    return allEntities.some((entity: { id: string }) => entity.id === entityId);
  };

  const entityTemplate = (rowData: OtherEntityAppointment) => {
    if (loading) return <Skeleton width="100%" />;
    const countryName = rowData?.entityData?.country
      ? getName(rowData.entityData.country) || rowData.entityData.country
      : null;

    const linkStyle = {
      color: "var(--button-link-color, #0B4720)",
      fontFamily: "Inter",
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: "var(--fontweight-regular, 400)" as const,
      textDecoration: "underline",
    };

    const content = (
      <>
        {rowData?.entityData?.name || "-"}
        {rowData?.entityData?.country && (
          <>
            {" ("}
            <ReactCountryFlag
              countryCode={rowData.entityData.country}
              svg
              style={{ width: "1em", height: "1em", marginRight: "0.25rem" }}
            />
            {countryName})
          </>
        )}
      </>
    );

    // Only show hyperlink if user has access to the entity
    const canAccessEntity = hasEntityAccess(rowData?.entity_id);

    return rowData?.entity_id && canAccessEntity ? (
      <Link to={`/entity/${rowData.entity_id}/dashboard`} style={linkStyle}>
        {content}
      </Link>
    ) : (
      <span>{content}</span>
    );
  };

  const statusTemplate = (rowData: OtherEntityAppointment) => {
    let className = "";
    switch (rowData?.status) {
      case "Active":
        className = "active-circle-fill";
        break;
      case "Removed":
        className = "removed-circle-fill";
        break;
      case "Not Active":
        className = "not-active-circle-fill";
        break;
      default:
        className = "not-active-circle-fill";
    }
    return loading ? (
      <Skeleton width="100%" />
    ) : (
      <div className="flex gap-1 align-items-center">
        <i className={`pi pi-circle-fill ${className}`} />
        {rowData?.status}
      </div>
    );
  };

  const actionMenuItems = (rowData: OtherEntityAppointment) => [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        onEditAppointment?.(rowData);
      },
    },
    {
      label: "Delete Appointment",
      icon: "pi pi-trash",
      command: () => {
        onDeleteAppointment?.(rowData);
      },
    },
  ];

  return (
    <div className="appointment-table-section flex flex-column gap-4">
      <DataTable
        value={loading ? skeletonRows : appointments}
        rowGroupMode="rowspan"
        paginator={appointments.length > 10}
        first={first}
        rows={rows}
        rowsPerPageOptions={[10, 20, 30]}
        onPage={onPageChange}
        className="datatable-right-align"
        emptyMessage="No results were found"
      >
        <Column
          field="entity_name"
          header="Entity"
          style={{ minWidth: "200px" }}
          body={entityTemplate}
        />
        <Column
          field="position"
          header="Position"
          style={{ minWidth: "150px" }}
          body={(rowData) =>
            loading ? <Skeleton width="100%" /> : rowData?.position || "-"
          }
        />
        <Column
          field="start_date"
          header="Appointment Date"
          style={{ minWidth: "150px" }}
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : rowData?.start_date ? (
              format(new Date(rowData.start_date), "MMM do, yyyy")
            ) : (
              "-"
            )
          }
        />
        <Column
          field="end_date"
          header="End Date"
          style={{ minWidth: "150px" }}
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : rowData?.end_date ? (
              format(new Date(rowData.end_date), "MMM do, yyyy")
            ) : (
              "-"
            )
          }
        />
        <Column
          field="status"
          header="Status"
          style={{ minWidth: "120px" }}
          body={statusTemplate}
        />
        <Column
          className="table-col-width"
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : (
              <AdminOnly>
                <div className="flex align-items-center justify-content-end gap-0">
                  <Menu menuItems={actionMenuItems(rowData)} />
                </div>
              </AdminOnly>
            )
          }
        />
      </DataTable>
    </div>
  );
};

export default OtherEntitiesAppointmentTable;
